import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiDownload, FiFilter, FiFileText } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatarData } from '../utils/dateUtils';

const ListaPassageiros = () => {
  const [passageiros, setPassageiros] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viagemFiltro, setViagemFiltro] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState(false);

  useEffect(() => {
    fetchViagens();
  }, []);

  const fetchViagens = async () => {
    try {
      const response = await axios.get('/api/viagens');
      setViagens(response.data);
    } catch (error) {
      toast.error('Erro ao carregar viagens');
    }
  };

  const fetchPassageiros = async (viagemId) => {
    if (!viagemId) {
      toast.error('Selecione uma viagem para buscar passageiros');
      return;
    }
    
    setLoading(true);
    try {
      const params = { viagem_id: viagemId };
      const response = await axios.get('/api/passageiros', { params });
      setPassageiros(response.data);
      setFiltroAplicado(true);
    } catch (error) {
      toast.error('Erro ao carregar passageiros');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroViagem = () => {
    if (!viagemFiltro) {
      toast.error('Selecione uma viagem para buscar');
      return;
    }
    fetchPassageiros(viagemFiltro);
  };

  const handleLimparFiltro = () => {
    setViagemFiltro('');
    setPassageiros([]);
    setFiltroAplicado(false);
  };

  const handleDownloadPDF = async () => {
    if (!viagemFiltro) {
      toast.error('Selecione uma viagem para gerar o PDF');
      return;
    }
    
    if (passageiros.length === 0) {
      toast.error('Nenhum passageiro encontrado para gerar o PDF');
      return;
    }

    try {
      const params = { viagem_id: viagemFiltro };
      const response = await axios.get('/api/export/passageiros/pdf', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const viagemNome = viagens.find(v => v.id === parseInt(viagemFiltro))?.titulo || 'viagem';
      const nomeArquivo = `lista_passageiros_${viagemNome.replace(/\s+/g, '_')}.pdf`;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF baixado com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
      console.error('Erro ao baixar PDF:', error);
    }
  };

  const viagemSelecionada = viagens.find(v => v.id === parseInt(viagemFiltro));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Lista de Passageiros</h1>
          <p className="text-white mt-1">Lista para controle da ANTT</p>
        </div>
        <button
          onClick={handleDownloadPDF}
          disabled={!viagemFiltro || passageiros.length === 0}
          className={`flex items-center px-4 py-2 rounded-lg ${
            !viagemFiltro || passageiros.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          <FiDownload className="mr-2" size={20} />
          Baixar PDF
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-sky-50 p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-800 mb-1">
              Filtrar por Viagem
            </label>
            <select
              value={viagemFiltro}
              onChange={(e) => setViagemFiltro(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
            >
              <option value="">Selecione uma viagem</option>
              {viagens.map((viagem) => (
                <option key={viagem.id} value={viagem.id}>
                  {viagem.titulo} - {formatarData(viagem.data_ida)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleFiltroViagem}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <FiFilter className="mr-2" size={18} />
              Filtrar
            </button>
            {filtroAplicado && (
              <button
                onClick={handleLimparFiltro}
                className="px-4 py-2 bg-white border border-sky-200 rounded-lg hover:bg-sky-100 text-gray-700"
              >
                Limpar
              </button>
            )}
          </div>
        </div>
        {viagemSelecionada && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-800">
              <strong>Viagem selecionada:</strong> {viagemSelecionada.titulo}
            </p>
            <p className="text-sm text-gray-700">
              Destino: {viagemSelecionada.destino} | Data: {formatarData(viagemSelecionada.data_ida)}
            </p>
          </div>
        )}
      </div>

      {/* Lista de Passageiros */}
      {!filtroAplicado ? (
        <div className="bg-sky-50 rounded-lg shadow p-12 text-center">
          <FiFileText className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 text-lg">Selecione uma viagem para visualizar os passageiros</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sky-200">
              <thead className="bg-sky-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Endereço
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Cidade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Telefone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    RG
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    CPF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Poltrona
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-sky-200">
                {passageiros.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-600 bg-white">
                      Nenhum passageiro encontrado para esta viagem
                    </td>
                  </tr>
                ) : (
                  passageiros.map((passageiro) => (
                    <tr key={passageiro.id} className="hover:bg-sky-100 bg-white">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {passageiro.nome}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {passageiro.endereco || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {passageiro.cidade || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {passageiro.telefone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {passageiro.rg || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {passageiro.cpf}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {passageiro.assento_numero ? `Poltrona ${passageiro.assento_numero}` : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {passageiros.length > 0 && (
            <div className="px-6 py-4 bg-sky-100 border-t border-sky-200">
              <p className="text-sm text-gray-700">
                Total de passageiros: <strong>{passageiros.length}</strong>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListaPassageiros;
